import React, { useRef } from 'react';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import AuthError from '../../AuthError/authError';
import { IAutocompleteData, IAutocompleteItem } from '../../../Interfaces/componentTypes';
import styles from '../AuthInput/authInputs.module.scss';
import { t } from 'i18next';

interface IAutoCompleteProps {
    data: IAutocompleteData | undefined;
    label?: string;
    disable?: boolean;
    id?: string;
    value?: IAutocompleteItem[]; 
    name?: string;
    onChange: (arg: IAutocompleteItem[]) => void; 
    placeholder?: string;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
    showTextError?: boolean;
    style?: string;
    labelStyle?: any;
}

const MultiSelectAutoComplete: React.FC<IAutoCompleteProps> = (props) => {
    const { data, label, error, onChange, id, placeholder, disable, value = [], showTextError = true, style, labelStyle } = props;
    const options = (data ?? []).filter((item) => !value.some((selected) => selected.id === item.id));
    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
        <div className={`${styles.container} ${style}`}>
            {label && (
                <label htmlFor={id} className={`${styles.label} ${labelStyle}`}>
                    {label}
                    {error && <span style={{ color: '#b00020', marginLeft: '5px' }}>*</span>}
                </label>
            )}
            <div>
                <Autocomplete
                    multiple
                    value={Array.isArray(value) ? value : []} 
                    disabled={disable}
                    disablePortal
                    id={id}
                    fullWidth
                    options={options}
                    noOptionsText={<span className={styles.noResult}>{t('No_Data.No_Search')}</span>}
                    getOptionLabel={(option) => option.title}
                    onChange={(_, selected) => {
                        onChange(selected || []);
                        if (inputRef.current) {
                            inputRef.current.blur();
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={inputRef}
                            placeholder={placeholder}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: 'gray',
                                    fontStyle: 'italic',
                                    transition: 'all 0.2s ease',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: 'var(--text-dark)' },
                                    '&.Mui-focused fieldset': { borderColor: 'var(--text-dark)', borderWidth: '1px' },
                                    ...(error && { '& fieldset': { borderColor: '#b00020' } }),
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
                            {option.icon && (
                                <span style={{ marginRight: '10px' }}>
                                    <img src={option.icon} alt={option.title} />
                                </span>
                            )}
                            {option.title}
                        </li>
                    )}
                />
            </div>
            {showTextError && <AuthError text={error?.message} />}
        </div>
    );
};

export default MultiSelectAutoComplete;
